import React, { memo } from "react";

import { ErrorBoundary } from "react-error-boundary";
import {
  ClassificationIcon,
  LoadingIndicator,
  NoData
} from "../../../../../../components";
import Grid from "../../../../../../components/Grid";
import { NetworkRepEntry } from "../../../../../network/@types/Url";
import commonStyles from "../../../../containers/report_summary/report-summary.module.scss";
import { Name } from "../../../../../search/schema/components";
import SampleReportGridLink from "../../../details/SampleReportGrid/SampleReportGridLink";
import { NetworkRepWithSource } from "../../@types";
import NetworkReferenceReputation, {
  NetworkReferenceReputationGridTitle
} from "./NetworkReferenceReputation";

type SchemaProps = {
  sourceId: number | undefined;
  relationshipId: number | undefined;
};

const NetworkReferenceErrorComp = () => (
  <span className={commonStyles.error_msg}>Network References Error</span>
);
const NetworkReferenceEmptyGridComp = () => (
  <span className={commonStyles.error_msg}>
    No available Network References
  </span>
);

const networkRefSchema = ({ sourceId, relationshipId }: SchemaProps) => ({
  fields: [
    {
      name: "status",
      displayTitle: (
        <span
          style={{ opacity: "0.3" }}
          className="threat threat-placeholder"
        />
      ),
      sortable: false,
      component: (source: NetworkRepEntry) => (
        <ClassificationIcon classification={source.classification} />
      ),
      style: {
        width: "5%"
      },
      className: "threat-table-cell"
    },
    {
      name: "value",
      displayTitle: "Value",
      sortable: false,
      component: (item: NetworkRepEntry) => (
        <Name
          data={{
            int_submission_analyzer: 1,
            classification: item?.classification,
            file_name: item?.requested_network_location
          }}
        />
      ),
      style: {
        width: "25%"
      }
    },
    {
      name: "source",
      displayTitle: "Source",
      sortable: false,
      component: (item: NetworkRepWithSource) =>
        [...new Set(item?.extra?.map(norm => norm.source) || [])].join(", "),
      style: {
        width: "20%"
      },
      className: "overflow-ellipsis"
    },
    {
      name: "file_protocol",
      displayTitle: "File",
      sortable: false,
      component: (item: NetworkRepWithSource) => {
        // remove non static analysis network reputations (interesting strings only)
        if (item.extra.every(e => !e.filename)) {
          return <NoData />;
        }

        const sourcesMap = new Map(item.extra.map(e => [e.filename, e]));
        const sources = Array.from(sourcesMap.values()).filter(
          source => source.filename
        );

        return (
          <div className="overflow-ellipsis k-pdf-network-references-files-list">
            {sources.map((source, index) => (
              <span key={source.filename}>
                <SampleReportGridLink
                  data={source}
                  sampleSourceID={sourceId}
                  relationshipID={relationshipId}
                  section={"ticore/interesting_strings"}
                />
                {index < sources.length - 1 && (
                  <span className={"k-pdf-hide-in-pdf"}>, </span>
                )}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      name: "categories",
      displayTitle: "Categories",
      sortable: false,
      component: (item: NetworkRepEntry) =>
        (item?.categories || []).join(", ") || <NoData />
    },
    {
      name: "reputation",
      displayTitle: NetworkReferenceReputationGridTitle,
      sortable: false,
      component: (item: NetworkRepEntry) => (
        <NetworkReferenceReputation networkRepEntry={item} />
      ),
      style: {
        width: "10%"
      }
    }
  ]
});

interface Props {
  data: NetworkRepWithSource[] | undefined;
  sourceId: number | undefined;
  relationshipId: number | undefined;
  isLoading: boolean;
  hidePaging: boolean;
  page?: number;
  pageSize?: number;
  totalResults?: number;
  numPages?: number;
  onPageChange?: (newPage: number, newPageSize: number) => void;
}

const NetworkReferencesGrid: React.FC<Props> = ({
  data,
  sourceId,
  relationshipId,
  isLoading,
  hidePaging,
  page,
  pageSize,
  totalResults,
  numPages,
  onPageChange
}) => (
  <div data-testid={"network-references-grid"}>
    <ErrorBoundary fallback={<NetworkReferenceErrorComp />}>
      <Grid
        data={data || []}
        hidePaging={hidePaging}
        enablePageSizeSelector
        schema={networkRefSchema({ sourceId, relationshipId })}
        showLoading={isLoading}
        emptyGridMsg={<NetworkReferenceEmptyGridComp />}
        page={page}
        pageSize={pageSize}
        totalResults={totalResults}
        numPages={numPages}
        onPageChange={onPageChange}
        showPageNumbers
        LoadingComponent={<LoadingIndicator style={{ margin: "15rem 0" }} />}
      />
    </ErrorBoundary>
  </div>
);

export default memo(NetworkReferencesGrid);
